import { useCallback, useMemo } from 'react';

import { MenuItem } from './DashboardMenuItem';
import { menuItems } from './menuItems';
import { useAbp } from 'core/hooks/useAbp';
import { useRole } from 'features/auth/contexts/RoleContext';
import { PM_NATURES } from 'features/access/api/getMyAccess';
import { useCurrentAccess } from 'features/access/hooks/useCurrentAccess';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { RoleNames, isSameRoleName } from 'core/roles/roleNames';
import { PortailNames } from 'core/roles/sessionContext';

/**
 * Filtrage des entrées de menu, partagé desktop/mobile (même politique partout) :
 * permission ABP accordée, visibilité par rôle actif (menuPolicy), et espace
 * courtier vs compagnie décidé par la CASQUETTE :
 * - items `courtierOnly` masqués dans l'espace compagnie (PM de nature Compagnie
 *   OU casquette Plateforme), sauf les listes du portefeuille (capability
 *   PlateformePortfolioLists) que le serveur borne en vue plateforme ;
 * - items `compagnieOnly` visibles pour une PM Compagnie OU pour un
 *   courtier-dirigeant sous casquette Plateforme (sa PM est la PM principale).
 */
export function useVisibleMenuItems(): { visibleItems: MenuItem[]; isLoading: boolean } {
  const { hasPermission } = useAbp();
  const { isMenuItemAllowed, session, isLoading: isRoleLoading } = useRole();
  const { access, plateformes } = useCurrentAccess();
  const canSeeOrgPortfolio = useRoleCan(Capability.PlateformePortfolioLists);

  const isCompagnie = access?.nature === PM_NATURES.COMPAGNIE;
  // Casquette plateforme : portail Plateforme en mode portail, rôle actif
  // Plateforme en mode legacy.
  const isPlateformeHat =
    session.mode === 'portail'
      ? session.portail === PortailNames.Plateforme
      : isSameRoleName(session.roleName, RoleNames.Plateforme);
  // Gouvernance réelle (pas la simple titularité) : une plateforme administrée
  // par l'admin ne donne aucun outil de gouvernance à son titulaire.
  const hasOwnedPlateforme = plateformes.some((o) => o.gouverne);
  const espaceCompagnie = isCompagnie || isPlateformeHat;
  const showCompagnieItems = isCompagnie || (isPlateformeHat && hasOwnedPlateforme);

  const filterMenuItems = useCallback(
    (items: MenuItem[]): MenuItem[] => {
      return items.filter((item) => {
        if (item.courtierOnly && espaceCompagnie && !canSeeOrgPortfolio) {
          return false;
        }
        if (item.compagnieOnly && !showCompagnieItems) {
          return false;
        }

        if (item.permission && !hasPermission(item.permission)) {
          return false;
        }

        if (!isMenuItemAllowed(item.id)) {
          return false;
        }

        if (item.children) {
          item.children = filterMenuItems(item.children);
          return item.to || item.children.length > 0;
        }

        return true;
      });
    },
    [hasPermission, isMenuItemAllowed, espaceCompagnie, showCompagnieItems, canSeeOrgPortfolio]
  );

  const visibleItems = useMemo(
    () => (isRoleLoading ? [] : filterMenuItems([...menuItems])),
    [filterMenuItems, isRoleLoading]
  );

  return { visibleItems, isLoading: isRoleLoading };
}
